// Кутийката с един показател — общата за модул „Справки".
//
// Иерархията е винаги една и съща: kicker (моно, главни, разреден), едро
// число, после ред с Δ чип и подтекст. Редът долу пази минимална височина,
// за да са равновисоки и картите без делта.

import type { ReactNode } from "react";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";

/** Минус знак U+2212 — по-широк от дефиса и подравнен с цифрите. */
const MINUS = "−";

/**
 * Δ спрямо предходния период.
 * `invert` е за показателите, при които спадът е добра новина (сторно,
 * отстъпки) — тогава зеленото и червеното си разменят местата.
 */
export const DeltaChip = ({
  value,
  invert = false,
}: {
  value: number | null | undefined;
  invert?: boolean;
}) => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }
  const isUp = value >= 0;
  const isGood = invert ? !isUp : isUp;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-[2px] font-pos-mono text-[11px] font-bold tabular-nums ${
        isGood ? "bg-ok-tint text-ok-deep" : "bg-danger-tint text-danger-ink"
      }`}
    >
      {isUp ? (
        <ArrowUpOutlined className="text-[8px]" />
      ) : (
        <ArrowDownOutlined className="text-[8px]" />
      )}
      {`${isUp ? "+" : MINUS}${Math.abs(value).toFixed(1)} %`}
    </span>
  );
};

type Props = {
  kicker: string;
  value: string;
  sub?: ReactNode;
  delta?: number | null;
  /** Спадът е добра новина (сторно, отстъпки). */
  invertDelta?: boolean;
  /** По-едро число — за водещия показател на екрана. */
  hero?: boolean;
  /** По-дребно число, което се реже с „…" — за тясна кутийка в модал
   *  (историята на госта в „Гости"), където 22px не се събират на телефон. */
  compact?: boolean;
  /** Стойността иска внимание (напр. сторно > 0) — рамка и цвят на числото. */
  hot?: boolean;
  onClick?: () => void;
  className?: string;
};

export const ReportKpiCard = ({
  kicker,
  value,
  sub,
  delta,
  invertDelta = false,
  hero = false,
  compact = false,
  hot = false,
  onClick,
  className = "",
}: Props) => {
  // Класовете са ЦЕЛИ литерали — Tailwind не вижда сглобени имена.
  const valueSize = hero
    ? "text-[26px] leading-none"
    : compact
      ? "max-w-full truncate text-[18px] leading-[1.2]"
      : "text-[22px] leading-none";
  const body = (
    <>
      <p className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {kicker}
      </p>
      <p
        className={`mb-1.5 mt-2 font-pos-mono font-extrabold tracking-[-0.02em] tabular-nums ${valueSize} ${
          hot ? "text-danger-ink" : "text-ink"
        }`}
      >
        {value}
      </p>
      <div className="flex min-h-4 flex-wrap items-center gap-2">
        {delta !== undefined ? (
          <DeltaChip value={delta} invert={invertDelta} />
        ) : null}
        {sub ? (
          <span className="text-[11.5px] font-medium leading-[1.2] text-muted">
            {sub}
          </span>
        ) : null}
      </div>
    </>
  );

  const shell = `flex min-w-0 flex-col items-start rounded-xl border bg-surface px-3.5 py-3 text-left ${
    hot ? "border-brand-tint2" : "border-shade-2"
  } ${className}`;

  if (!onClick) {
    return <article className={shell}>{body}</article>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${shell} transition-colors hover:bg-fill`}
    >
      {body}
    </button>
  );
};
